import React, { useState } from "react";
import { Ban, Check, Loader, Minus, Plus, RotateCcw, TriangleAlert } from "lucide-react";
import { useSearchParams } from "wouter";

import {
    BankAccountBalance,
    BankAccountTransactionInput,
    BankAccountTransactionStatusEnum,
    MatchBankAccountTransactionsInput,
    MatchingStatus,
    Position,
    Transaction,
    TransactionStatus,
    UpdateTransactionInput,
    UpdateTransactionItemInput
} from "../types.generated";
import { YesNoModal } from "../../../components/src/YesNoModal";
import { ITransactionDataRow, TransactionsGrid } from "../components/TransactionsGrid";
import { cloneDeep, round, sortBy } from "lodash";
import { Column, Grid } from "../../../components/src";
import { twoDecPriceFormat } from "../../../common/src/utils/FormatFunctions";
import { DateHelper } from "../../../common/src";
import { ICommandBarMenuItem } from "../../../components/src/common/CommandBar";

interface IUpdateTransactionsStatusGrid {
    itemsByGroup: Record<string, ITransactionDataRow[]>;
    itemsReconcileByGroup: Record<string, ITransactionDataRow[]>;
    visibleColumns?: string[];
    visibleReconcileColumns?: string[];
    onUpdate(input: UpdateTransactionInput[]): any;
    onUpdateReconcileStatus(input: Partial<BankAccountTransactionInput>[]): any;
    onMatch(input: MatchBankAccountTransactionsInput[]): any;
    header?: string;
    headerReconcile?: string;
    headerGroups?: string[];
    spinner: boolean;
    transactionsByItemId: Record<string, Partial<Transaction>>;
    positionsByInstrumentId: Record<string, Partial<Position>>;
    bankBalancesByInstrumentId: Record<string, Partial<BankAccountBalance>>;
    instrumentId: string;
}

export const BankAccountReconciliationGrid = ({
    itemsByGroup,
    itemsReconcileByGroup,
    visibleColumns,
    visibleReconcileColumns,
    onUpdate,
    onUpdateReconcileStatus,
    onMatch,
    header,
    headerReconcile,
    headerGroups,
    spinner,
    transactionsByItemId,
    positionsByInstrumentId,
    instrumentId,
    bankBalancesByInstrumentId
}: IUpdateTransactionsStatusGrid): React.ReactElement => {
    const [queryArgs] = useSearchParams();
    const [selectedByGroup, setSelectedByGroup] = useState({});
    const [selectedReconcileByGroup, setSelectedReconcileByGroup] = useState({});
    const [modal, setModal] = useState({ showModal: false, payload: { input: [], transactions: true } });
    const [showDataByStatusGroup, setShowDataByStatusGroup] = useState({});
    const [showReconcileDataByStatusGroup, setShowReconcileDataByStatusGroup] = useState({});

    const today = new Date().toISOString().slice(0, 10);

    const selectedChange = (items: any[]) => {
        const newSelectedData: Record<string, ITransactionDataRow[]> = {};
        for (const item of items) {
            let status = item.status;

            if (item.status === TransactionStatus.Settled) {
                status = item.status;
            } else {
                if (item.valueDate > today) {
                    status = "Not settled - future";
                } else {
                    status = "Not settled";
                }
            }

            if (newSelectedData[status]) {
                newSelectedData[status].push(item);
            } else {
                newSelectedData[status] = [item];
            }
        }

        setSelectedByGroup(newSelectedData);
    };

    const selectedChangeReconcile = (reconcileItems: any[]) => {
        const newSelectedData: Record<string, ITransactionDataRow[]> = {};
        for (const reconcileItem of reconcileItems) {
            let status = null;

            if (reconcileItem.status === BankAccountTransactionStatusEnum.Settled) {
                status = reconcileItem.status;
            } else {
                if (reconcileItem.valueDate > today) {
                    status = "Not settled - future";
                } else {
                    status = "Not settled";
                }
            }

            if (newSelectedData[status]) {
                newSelectedData[status].push(reconcileItem);
            } else {
                newSelectedData[status] = [reconcileItem];
            }
        }

        setSelectedReconcileByGroup(newSelectedData);
    };

    let commands: ICommandBarMenuItem[] = [
        {
            itemKey: "newItem",
            text: "Change status/valueDate",
            subMenuProps: {
                items: [
                    {
                        itemKey: "pending",
                        text: "Change to Pending",
                        icon: <RotateCcw />,
                        onClick: () => {
                            const input = Object.values(selectedByGroup)
                                .flat(1)
                                .map((t: any) => {
                                    return { _id: t._id, status: TransactionStatus.Pending };
                                });
                            onUpdate(input);
                            setSelectedByGroup({});
                        }
                    },
                    {
                        itemKey: "preliminary",
                        text: "Change to Preliminary",
                        icon: <TriangleAlert />,
                        onClick: () => {
                            const input = Object.values(selectedByGroup)
                                .flat(1)
                                .map((t: any) => {
                                    return { _id: t._id, status: TransactionStatus.Preliminary };
                                });
                            onUpdate(input);
                            setSelectedByGroup({});
                        }
                    },
                    {
                        itemKey: "confirmed",
                        text: "Change to Confirmed",
                        icon: <Check />,
                        onClick: () => {
                            const input = Object.values(selectedByGroup)
                                .flat(1)
                                .map((t: any) => {
                                    return { _id: t._id, status: TransactionStatus.Confirmed };
                                });
                            onUpdate(input);
                            setSelectedByGroup({});
                        }
                    },
                    {
                        itemKey: "instructed",
                        text: "Change to Instructed",
                        onClick: () => {
                            const input = Object.values(selectedByGroup)
                                .flat(1)
                                .map((t: any) => {
                                    return { _id: t._id, status: TransactionStatus.Instructed };
                                });
                            onUpdate(input);
                            setSelectedByGroup({});
                        }
                    },
                    {
                        itemKey: "settled",
                        text: "Change to Settled",
                        onClick: () => {
                            const input = Object.values(selectedByGroup)
                                .flat(1)
                                .map((t: any) => {
                                    return { _id: t._id, status: TransactionStatus.Settled };
                                });
                            onUpdate(input);
                            setSelectedByGroup({});
                        }
                    },
                    {
                        itemKey: "internal",
                        text: "Change to Internal",
                        onClick: () => {
                            const input = Object.values(selectedByGroup)
                                .flat(1)
                                .map((t: any) => {
                                    return { _id: t._id, status: TransactionStatus.Internal };
                                });
                            onUpdate(input);
                            setSelectedByGroup({});
                        }
                    },
                    {
                        itemKey: "history",
                        text: "Change to History",
                        onClick: () => {
                            const input = Object.values(selectedByGroup)
                                .flat(1)
                                .map((t: any) => {
                                    return { _id: t._id, status: TransactionStatus.History };
                                });
                            onUpdate(input);
                            setSelectedByGroup({});
                        }
                    },
                    {
                        itemKey: "deleted",
                        text: "Change to Deleted",
                        icon: <Ban />,
                        onClick: () => {
                            const input = Object.values(selectedByGroup)
                                .flat(1)
                                .map((t: any) => {
                                    return { _id: t._id, status: TransactionStatus.Deleted };
                                });
                            setModal({
                                showModal: true,
                                payload: { input: input, transactions: true }
                            });
                        }
                    },
                    {
                        itemKey: "oneday",
                        text: "Set valueDate - 1 day",
                        onClick: () => {
                            if (transactionsByItemId && Object.keys(transactionsByItemId).length) {
                                const input = Object.values(selectedByGroup)
                                    .flat(1)
                                    .map((balanceItem: any) => {
                                        const transaction = transactionsByItemId[balanceItem.transactionItemId];

                                        const previousDay = DateHelper.dateAddDays(new Date(transaction.items[0].valueDate), -1);
                                        const previousDayString = DateHelper.dateToString(previousDay).substring(0, 10);
                                        const newItems: UpdateTransactionItemInput[] = [];

                                        for (const item of transaction.items) {
                                            const newItem = {
                                                _id: item._id,
                                                valueDate: previousDayString,
                                                instrumentId: item.instrumentId,
                                                type: item.type
                                            };

                                            newItems.push(newItem);
                                        }
                                        return { _id: transaction._id, items: newItems };
                                    });
                                onUpdate(input);
                            }

                            setSelectedByGroup({});
                        }
                    },
                    {
                        itemKey: "twodays",
                        text: "Set valueDate - 2 days",
                        onClick: () => {
                            if (transactionsByItemId && Object.keys(transactionsByItemId).length) {
                                const input = Object.values(selectedByGroup)
                                    .flat(1)
                                    .map((balanceItem: any) => {
                                        const transaction = transactionsByItemId[balanceItem.transactionItemId];
                                        const previousPreviousDay = DateHelper.dateAddDays(new Date(transaction.items[0].valueDate), -2);
                                        const previousPreviousDayString = DateHelper.dateToString(previousPreviousDay).substring(0, 10);
                                        const newItems: UpdateTransactionItemInput[] = [];

                                        for (const item of transaction.items) {
                                            const newItem = {
                                                _id: item._id,
                                                valueDate: previousPreviousDayString,
                                                instrumentId: item.instrumentId,
                                                type: item.type
                                            };

                                            newItems.push(newItem);
                                        }
                                        return { _id: transaction._id, items: newItems };
                                    });
                                onUpdate(input);
                            }
                            setSelectedByGroup({});
                        }
                    },
                    {
                        itemKey: "threedays",
                        text: "Set valueDate - 3 days",
                        onClick: () => {
                            if (transactionsByItemId && Object.keys(transactionsByItemId).length) {
                                const input = Object.values(selectedByGroup)
                                    .flat(1)
                                    .map((balanceItem: any) => {
                                        const transaction = transactionsByItemId[balanceItem.transactionItemId];
                                        const thirdPreviousDay = DateHelper.dateAddDays(new Date(transaction.items[0].valueDate), -3);
                                        const thirdPreviousDayString = DateHelper.dateToString(thirdPreviousDay).substring(0, 10);
                                        const newItems: UpdateTransactionItemInput[] = [];

                                        for (const item of transaction.items) {
                                            const newItem = {
                                                _id: item._id,
                                                valueDate: thirdPreviousDayString,
                                                instrumentId: item.instrumentId,
                                                type: item.type
                                            };

                                            newItems.push(newItem);
                                        }
                                        return { _id: transaction._id, items: newItems };
                                    });
                                onUpdate(input);
                            }
                            setSelectedByGroup({});
                        }
                    }
                ]
            }
        }
    ];

    let commandsReconcile: ICommandBarMenuItem[] = [
        {
            itemKey: "newItem",
            text: "Change status",
            subMenuProps: {
                items: [
                    {
                        itemKey: "pending",
                        text: "Change to Pending",
                        icon: <RotateCcw />,
                        onClick: () => {
                            const input = Object.values(selectedReconcileByGroup)
                                .flat(1)
                                .map((t: any) => {
                                    return { _id: t._id, status: BankAccountTransactionStatusEnum.Pending };
                                });
                            onUpdateReconcileStatus(input);
                            setSelectedReconcileByGroup({});
                        }
                    },
                    {
                        itemKey: "settled",
                        text: "Change to Settled",
                        onClick: () => {
                            const input = Object.values(selectedReconcileByGroup)
                                .flat(1)
                                .map((t: any) => {
                                    return { _id: t._id, status: BankAccountTransactionStatusEnum.Settled };
                                });
                            onUpdateReconcileStatus(input);
                            setSelectedReconcileByGroup({});
                        }
                    },
                    {
                        itemKey: "deleted",
                        text: "Change to Deleted",
                        icon: <Ban />,
                        onClick: () => {
                            const input = Object.values(selectedReconcileByGroup)
                                .flat(1)
                                .map((t: any) => {
                                    return { _id: t._id, status: BankAccountTransactionStatusEnum.Deleted };
                                });
                            setModal({
                                showModal: true,
                                payload: { input: input, transactions: false }
                            });
                        }
                    }
                ]
            }
        }
    ];

    // Only show below if not multiple of each type selected + none have status Settled
    if (
        Object.keys(selectedByGroup) &&
        Object.keys(selectedReconcileByGroup) &&
        ((Object.values(selectedByGroup).flat(1).length === 1 && Object.values(selectedReconcileByGroup).flat(1).length >= 1) ||
            (Object.values(selectedReconcileByGroup).flat(1).length === 1 && Object.values(selectedByGroup).flat(1).length >= 1))
    ) {
        const selected = Object.values(selectedByGroup).flat(1) as any[];
        const selectedReconcile = Object.values(selectedReconcileByGroup).flat(1) as any[];
        let matchInput: MatchBankAccountTransactionsInput[] = [];
        if (selected.length === 1) {
            const reconcileIds: string[] = [];
            let reconcileItemSettledFound = false;
            for (const reconcileItem of selectedReconcile) {
                reconcileIds.push(reconcileItem._id);
                if (reconcileItem.status === BankAccountTransactionStatusEnum.Settled) {
                    reconcileItemSettledFound = true;
                }
            }

            if (selected[0].status !== TransactionStatus.Settled && !reconcileItemSettledFound) {
                matchInput = [
                    {
                        bankAccountTransactionIds: selectedReconcile.map((transaction) => {
                            return transaction._id;
                        }),
                        bankAccountTransactionError: null,
                        transactionIdentifierIds: [{ transactionId: selected[0]._id, transactionItemId: selected[0].transactionItemId }],
                        transactionItemError: "",
                        matchingStatus: MatchingStatus.Confirmed
                    }
                ];
            }
        } else {
            const identifierIds: { transactionId: string; transactionItemId: string }[] = [];
            let itemSettledFound = false;
            for (const item of selected) {
                identifierIds.push({ transactionId: item._id, transactionItemId: item.transactionItemId });
                if (item.status === TransactionStatus.Settled) {
                    itemSettledFound = true;
                }
            }
            if (selectedReconcile[0].status !== BankAccountTransactionStatusEnum.Settled && !itemSettledFound) {
                matchInput = [
                    {
                        bankAccountTransactionIds: [selectedReconcile[0]._id],
                        bankAccountTransactionError: null,
                        transactionIdentifierIds: identifierIds,
                        transactionItemError: "",
                        matchingStatus: MatchingStatus.Confirmed
                    }
                ];
            }
        }

        if (matchInput.length) {
            commands = [
                {
                    itemKey: "matchTransactionMenuItem",
                    text: "Match",
                    onClick: () => {
                        onMatch(matchInput);
                        setSelectedByGroup({});
                        setSelectedReconcileByGroup({});
                    }
                }
            ];

            commandsReconcile = [
                {
                    itemKey: "matchTransactionMenuItem",
                    text: "Match",
                    onClick: () => {
                        onMatch(matchInput);
                        setSelectedByGroup({});
                        setSelectedReconcileByGroup({});
                    }
                }
            ];
        } else {
            // cannot update status for transactions + bank account transactions at the same time
            commands = [];
            commandsReconcile = [];
        }
    }

    const settledBalance =
        positionsByInstrumentId && positionsByInstrumentId[instrumentId] ? positionsByInstrumentId[instrumentId].amount : 0;
    const settledBankBalance =
        bankBalancesByInstrumentId && bankBalancesByInstrumentId[instrumentId]
            ? parseFloat(bankBalancesByInstrumentId[instrumentId].balance)
            : 0;

    const balanceCheck = [
        {
            settledBalance: settledBalance,
            settledBankBalance: settledBankBalance,
            diff: settledBalance - settledBankBalance
        }
    ];

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete selected item(s)?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: { input: modal.payload.input, transactions: modal.payload.transactions }
                    }}
                    setModal={setModal}
                    onYes={() => {
                        if (modal.payload.transactions) {
                            onUpdate(modal.payload.input as unknown as UpdateTransactionInput[]);
                            setSelectedByGroup({});
                        } else {
                            onUpdateReconcileStatus(modal.payload.input as unknown as Partial<BankAccountTransactionInput>[]);
                            setSelectedReconcileByGroup({});
                        }
                    }}
                    onNo={() => {
                        // disable eslint
                    }}
                />
            ) : null}
            <div className={"flex flex-wrap -mx-2 justify-center mt-4"}>
                <div className="w-full xl:w-1/4 px-2 mb-4">
                    <div>
                        <Grid data={balanceCheck} sortable tableClassName="table-xs" hideDownload={true}>
                            <Column className="nowrap" format={twoDecPriceFormat} field="settledBalance" title="Settled balance"></Column>
                            <Column
                                className="nowrap"
                                format={twoDecPriceFormat}
                                field="settledBankBalance"
                                title="Settled bank balance"
                            ></Column>
                            <Column
                                field="diff"
                                format={twoDecPriceFormat}
                                className={(d) => {
                                    if (d && d.diff && Math.abs(round(d.diff, 2)) !== 0) {
                                        return "right nowrap btn-sm btn-danger text-white";
                                    } else {
                                        return "right nowrap ";
                                    }
                                }}
                            ></Column>
                        </Grid>
                    </div>
                </div>
            </div>
            <div className="mt-4">
                <div className="flex flex-wrap -mx-2">
                    <div className="w-full md:w-1/2 px-2 mb-4">
                        <h2>{header}</h2>
                    </div>

                    <div className="w-full md:w-1/2 px-2 mb-4">
                        <h2>{headerReconcile}</h2>
                    </div>
                </div>
                <div>
                    {spinner ? (
                        <span>
                            <Loader /> Updating status...
                        </span>
                    ) : null}
                </div>
                {headerGroups.map((statusGroup) => (
                    <div key={statusGroup} className="mt-4 mb-4">
                        <div className="text-center">
                            <h4>{statusGroup}</h4>
                        </div>
                        <>
                            <div className="flex flex-wrap -mx-2 mt-2">
                                <div className="w-full md:w-1/2 px-2 mb-4">
                                    {itemsByGroup && Object.keys(itemsByGroup) && itemsByGroup[statusGroup] ? (
                                        <div>
                                            <div>
                                                {showDataByStatusGroup && showDataByStatusGroup[statusGroup] ? (
                                                    <div>
                                                        <TransactionsGrid
                                                            key={`${JSON.stringify(itemsByGroup[statusGroup])}-${queryArgs.toString()}-${showDataByStatusGroup[statusGroup]}`}
                                                            items={itemsByGroup[statusGroup]}
                                                            onSelectedChange={selectedChange}
                                                            commands={selectedByGroup[statusGroup] ? commands : []}
                                                            visibleColumns={visibleColumns}
                                                            showExcelButton={false}
                                                        />
                                                        <Minus
                                                            className="mt-5 cursor-pointer"
                                                            size={16}
                                                            onClick={() => {
                                                                const newShowDataByStatusGroup = cloneDeep(showDataByStatusGroup);
                                                                newShowDataByStatusGroup[statusGroup] = false;
                                                                setShowDataByStatusGroup(newShowDataByStatusGroup);
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <TransactionsGrid
                                                            key={`${JSON.stringify(itemsByGroup[statusGroup])}-${queryArgs.toString()}`}
                                                            items={
                                                                statusGroup === "Not settled - future"
                                                                    ? itemsByGroup[statusGroup].slice(-15) // showing the most recent ones that are not settled
                                                                    : itemsByGroup[statusGroup].slice(0, 16)
                                                            }
                                                            onSelectedChange={selectedChange}
                                                            commands={selectedByGroup[statusGroup] ? commands : []}
                                                            visibleColumns={visibleColumns}
                                                            showExcelButton={false}
                                                        />
                                                        {itemsByGroup[statusGroup].length > 15 ? (
                                                            <Plus
                                                                className="mt-5 cursor-pointer"
                                                                size={16}
                                                                onClick={() => {
                                                                    const newShowDataByStatusGroup = cloneDeep(showDataByStatusGroup);
                                                                    newShowDataByStatusGroup[statusGroup] = true;
                                                                    setShowDataByStatusGroup(newShowDataByStatusGroup);
                                                                }}
                                                            />
                                                        ) : null}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                                <div className="w-full md:w-1/2 px-2 mb-4">
                                    {itemsReconcileByGroup && Object.keys(itemsReconcileByGroup) && itemsReconcileByGroup[statusGroup] ? (
                                        <div>
                                            <div>
                                                {showReconcileDataByStatusGroup && showReconcileDataByStatusGroup[statusGroup] ? (
                                                    <div>
                                                        <TransactionsGrid
                                                            key={`${JSON.stringify(sortBy(itemsReconcileByGroup[statusGroup], "valueDate").reverse())}-${queryArgs.toString()}-${showReconcileDataByStatusGroup[statusGroup]}`}
                                                            items={sortBy(itemsReconcileByGroup[statusGroup], "valueDate").reverse()}
                                                            onSelectedChange={selectedChangeReconcile}
                                                            commands={selectedReconcileByGroup[statusGroup] ? commandsReconcile : []}
                                                            visibleColumns={visibleReconcileColumns}
                                                            showExcelButton={false}
                                                            linkDescriptionToBankAccountTransaction
                                                        />
                                                        <Minus
                                                            className="mt-5 cursor-pointer"
                                                            size={16}
                                                            onClick={() => {
                                                                const newShowReconcileDataByStatusGroup =
                                                                    cloneDeep(showReconcileDataByStatusGroup);
                                                                newShowReconcileDataByStatusGroup[statusGroup] = false;
                                                                setShowReconcileDataByStatusGroup(newShowReconcileDataByStatusGroup);
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <TransactionsGrid
                                                            key={`${JSON.stringify(sortBy(itemsReconcileByGroup[statusGroup], "valueDate").reverse())}-${queryArgs.toString()}`}
                                                            items={sortBy(itemsReconcileByGroup[statusGroup], "valueDate")
                                                                .reverse()
                                                                .slice(0, 16)}
                                                            onSelectedChange={selectedChangeReconcile}
                                                            commands={selectedReconcileByGroup[statusGroup] ? commandsReconcile : []}
                                                            visibleColumns={visibleReconcileColumns}
                                                            showExcelButton={false}
                                                            linkDescriptionToBankAccountTransaction
                                                        />
                                                        {itemsReconcileByGroup[statusGroup].length > 15 ? (
                                                            <Plus
                                                                className="mt-5 cursor-pointer"
                                                                size={16}
                                                                onClick={() => {
                                                                    const newShowReconcileDataByStatusGroup =
                                                                        cloneDeep(showReconcileDataByStatusGroup);
                                                                    newShowReconcileDataByStatusGroup[statusGroup] = true;
                                                                    setShowReconcileDataByStatusGroup(newShowReconcileDataByStatusGroup);
                                                                }}
                                                            />
                                                        ) : null}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </>
                    </div>
                ))}
            </div>
        </div>
    );
};
